
import React, { useState, useEffect } from 'react';
import { X, Trash2, Calendar, Database, AlertTriangle, CheckSquare, Square } from 'lucide-react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  availableMonths: string[];
  onDelete: (month: string, targets: string[]) => Promise<void>;
}

const DeleteDataModal: React.FC<Props> = ({ isOpen, onClose, availableMonths, onDelete }) => {
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [targets, setTargets] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen) {
      if (availableMonths.length > 0) {
        setSelectedMonth(availableMonths[0]);
      } else {
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        setSelectedMonth(`${yyyy}/${mm}`);
      }
      setTargets(new Set());
    }
  }, [isOpen, availableMonths]);

  const toggleTarget = (key: string) => {
    const newTargets = new Set(targets);
    if (newTargets.has(key)) {
      newTargets.delete(key);
    } else {
      newTargets.add(key);
    }
    setTargets(newTargets);
  };

  const handleExecute = () => {
    if (!selectedMonth || targets.size === 0) return;
    
    // 即座に閉じる（UX向上）
    onClose();
    
    // 削除処理を開始（完了を待たずに閉じることで、ダッシュボード側のローディングを表示させる）
    onDelete(selectedMonth, Array.from(targets));
  };

  if (!isOpen) return null;

  const targetOptions = [
    { key: 'billing', label: '請求データ', desc: 'インポートされた請求残高データ' },
    { key: 'bank', label: '銀行データ', desc: '銀行FBまたは手動登録された入金データ' },
    { key: 'union', label: '組合データ', desc: '組合明細書（バッチおよび明細行）' },
  ];

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all scale-100">
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
          <h2 className="text-base font-black text-gray-800 flex items-center gap-2">
            <Trash2 size={20} className="text-red-500" />
            データ削除
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="bg-red-50 border border-red-100 rounded-lg p-3 flex items-start gap-3">
            <AlertTriangle size={18} className="text-red-500 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-red-700 leading-relaxed">
              <span className="font-bold block mb-1">ご注意ください</span>
              削除されたデータは復元できません。指定した月の対象データが完全に消去されます。
            </div>
          </div>

          <div>
            <label className="block text-xs font-black text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
              <Calendar size={14} /> 対象月
            </label>
            <div className="relative">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-full appearance-none bg-white border border-gray-300 text-gray-800 text-sm font-bold rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none shadow-sm transition-shadow"
              >
                {availableMonths.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500">
                <svg className="h-4 w-4 fill-current" viewBox="0 0 20 20"><path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"/></svg>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-black text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1">
              <Database size={14} /> 削除するデータ種別
            </label>
            <div className="space-y-2">
              {targetOptions.map((opt) => {
                const isChecked = targets.has(opt.key);
                return (
                  <div 
                    key={opt.key}
                    onClick={() => toggleTarget(opt.key)}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${isChecked ? 'bg-red-50 border-red-200 shadow-inner' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
                  >
                    <div className={`text-red-600 transition-transform ${isChecked ? 'scale-110' : 'scale-100 opacity-30'}`}>
                      {isChecked ? <CheckSquare size={20} /> : <Square size={20} />}
                    </div>
                    <div>
                      <div className={`text-sm font-bold ${isChecked ? 'text-red-800' : 'text-gray-700'}`}>{opt.label}</div>
                      <div className="text-[10px] text-gray-400">{opt.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
          <button 
            onClick={onClose} 
            className="px-5 py-2.5 rounded-lg border border-gray-300 text-gray-600 font-bold text-xs hover:bg-white hover:shadow-sm transition-all"
          >
            キャンセル
          </button>
          <button 
            onClick={handleExecute} 
            disabled={targets.size === 0 || !selectedMonth} 
            className="px-8 py-2.5 rounded-lg bg-red-600 text-white font-black text-xs hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-md transition-all active:scale-95 flex items-center gap-2"
          >
            削除を実行
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteDataModal;
